import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EmailIcon from '@mui/icons-material/Email';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

const BookingConfirmation = ({ bookingData, variant = 'default' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const isConfirmed = bookingData.confirmationMessage && bookingData.confirmationMessage.toLowerCase().includes('confirmed');
  const title = isConfirmed ? 'Booking Confirmed!' : 'Booking Request Received';
  const Icon = isConfirmed ? CheckCircleOutlineIcon : HourglassTopIcon;
  const iconColor = isConfirmed ? 'success.main' : 'warning.main';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ 
          mb: 3, 
          overflow: 'hidden',
          bgcolor: variant === 'home' ? 'transparent' : 'background.default',
        }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Icon 
              sx={{ 
                fontSize: 64, 
                color: variant === 'home' ? 'common.white' : iconColor,
                mb: 2 
              }} 
            />
            <Typography variant="h4" gutterBottom sx={{ 
              fontWeight: 600,
              color: variant === 'home' ? 'common.white' : 'text.primary',
              mb: 2
            }}>
              {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              mb: 3,
              color: variant === 'home' ? 'common.white' : 'text.secondary'
            }}>
              {bookingData.confirmationMessage}
              {bookingData.pickup.hasReturnJourney && (
                <Box component="span" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  This booking includes both outbound and return journeys.
                </Box>
              )}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 2,
              mb: 3
            }}>
              <ConfirmationNumberIcon sx={{ 
                color: variant === 'home' ? 'common.white' : 'primary.main'
              }} />
              <Typography variant="h6" sx={{ 
                color: variant === 'home' ? 'common.white' : 'text.primary'
              }}>
                Booking Reference: {bookingData.reference}
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 1,
              color: variant === 'home' ? 'common.white' : 'success.main'
            }}>
              <EmailIcon />
              <Typography variant="body1" sx={{
                color: variant === 'home' ? 'common.white' : 'text.primary'
              }}>
                A confirmation email has been sent to {bookingData.customer.email}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{
          bgcolor: variant === 'home' ? 'background.paper' : 'background.default'
        }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ 
              mb: 3,
              color: variant === 'home' ? 'text.primary' : 'text.primary'
            }}>
              Booking Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary'
                    }}>
                      <DirectionsCarIcon />
                      Vehicle
                    </Typography>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        {bookingData.vehicle.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary'
                    }}>
                      <LocationOnIcon />
                      Journey Details
                    </Typography>
                    
                    {/* Outbound Journey */}
                    <Box sx={{ pl: 4, mb: bookingData.pickup.hasReturnJourney ? 2 : 0 }}>
                      {bookingData.pickup.hasReturnJourney && (
                        <Typography variant="subtitle2" sx={{ 
                          color: 'primary.main', 
                          fontWeight: 600, 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          🚗 Outbound Journey
                        </Typography>
                      )}
                      <Box sx={{ pl: bookingData.pickup.hasReturnJourney ? 2 : 0 }}>
                        <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                          From: {bookingData.pickup.location}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.primary' }}>
                          To: {bookingData.pickup.dropoff}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                          Pickup Date & Time: {new Date(bookingData.pickup.dateTime).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }).replace(',', '')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Return Journey */}
                    {bookingData.pickup.hasReturnJourney && (
                      <Box sx={{ pl: 4 }}>
                        <Typography variant="subtitle2" sx={{ 
                          color: 'success.main', 
                          fontWeight: 600, 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          🔄 Return Journey
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                            From: {bookingData.pickup.returnLocation}
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.primary' }}>
                            To: {bookingData.pickup.returnDropoff}
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.primary', mt: 1 }}>
                            Return Date & Time: {new Date(bookingData.pickup.returnDateTime).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }).replace(',', '')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Stops */}
                    {bookingData.pickup.stops && bookingData.pickup.stops.length > 0 && (
                      <Box sx={{ pl: 4, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ 
                          color: 'info.main', 
                          fontWeight: 600, 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          🛑 Stops
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          {bookingData.pickup.stops.map((stop, index) => (
                            <Typography key={index} variant="body1" sx={{ color: 'text.primary', mb: 0.5 }}>
                              Stop {index + 1}: {stop.location}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary'
                    }}>
                      <AccessTimeIcon />
                      Duration & Passengers
                    </Typography>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                        Duration: {bookingData.pickup.duration} hours
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        Passengers: {bookingData.pickup.passengers}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary'
                    }}>
                      Customer Details
                    </Typography>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                        Name: {bookingData.customer.firstName} {bookingData.customer.lastName}
                      </Typography>
                      <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                        Email: {bookingData.customer.email}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        Phone: {bookingData.customer.phone}
                      </Typography>
                    </Box>
                  </Box>

                  {bookingData.customer.notes && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.primary'
                      }}>
                        Special Requests
                      </Typography>
                      <Box sx={{ pl: 4 }}>
                        <Typography variant="body1" sx={{ color: 'text.primary' }}>
                          {bookingData.customer.notes}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle1" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary'
                    }}>
                      Price Details
                    </Typography>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                        Total Price: <Box component="span" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '1.25rem' }}>£{bookingData.pricing ? bookingData.pricing.finalPrice.toFixed(2) : bookingData.price.toFixed(2)}</Box>
                        {bookingData.pickup.hasReturnJourney && (
                          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>
                            (both journeys)
                          </Box>
                        )}
                      </Typography>
                      {bookingData.pickup.duration > 2 && (
                        <Typography variant="body2" color="success.main">
                          10% discount applied
                        </Typography>
                      )}
                      {bookingData.pickup.hasReturnJourney && (
                        <Typography variant="body2" color="success.main">
                          10% return journey discount applied
                        </Typography>
                      )}
                      {bookingData.discountInfo && (
                        <Typography variant="body2" color="success.main">
                          Discount Code: {bookingData.discountInfo.message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom sx={{ color: 'text.primary' }}>
                Need to make changes to your booking?
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                Contact us at info@sls-services.com or WhatsApp 07542454026
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default BookingConfirmation; 